import { useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import type { AuthConfig } from "../lib/types";
import { useAuth } from "./AuthContext";
import { PasswordFields } from "./PasswordFields";
import { passwordsAcceptable } from "./password";

/**
 * ── Four screens, one card ─────────────────────────────────────────────────
 *
 *   password  email + password              → a challenge and an SMS
 *   otp       the texted code               → a session
 *   forgot    email only                    → a challenge and an SMS
 *   reset     the code + a new password     → a session
 *
 * There is no registration form and no link to one: an admin account exists
 * because the master invited it, or because someone with shell access seeded
 * it, never because a person arrived here with a company email address.
 *
 * The two halves are deliberately symmetrical. Both prove the phone; the
 * difference is only what they prove first — the current password, or nothing
 * at all. That asymmetry is why the API tags each challenge with a purpose and
 * refuses to redeem one at the other's endpoint: an email address alone must
 * not be able to mint a session.
 */
type Mode = "password" | "otp" | "forgot" | "reset";

export function LoginPage() {
  const { signIn, verifyOtp, resendOtp, forgotPassword, resetPassword } =
    useAuth();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [mode, setMode] = useState<Mode>("password");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");

  // Present only mid-ceremony. Held in state rather than storage on purpose:
  // a refresh should drop back to the start, not resume a half-finished
  // sign-in from a tab someone left open on a shared machine.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [phoneHint, setPhoneHint] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api<AuthConfig>("/api/admin/auth/config")
      .then(setConfig)
      .catch(() =>
        setError(
          "Can't reach the API. Check VITE_API_URL and that the server is running.",
        ),
      );
  }, []);

  // Moving focus is the whole difference between "type the code" and "find
  // the box, then type the code" on a phone.
  useEffect(() => {
    if (mode === "otp") codeInput.current?.focus();
  }, [mode]);

  function goTo(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setCode("");
    if (next === "password" || next === "forgot") {
      setChallenge(null);
      setPhoneHint(null);
      setPassword("");
      setConfirm("");
    }
  }

  /** Every submit shares this shape: clear, run, report, release. */
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const result = await signIn(email, password);
      if (result.step === "otp") {
        setChallenge(result.challenge);
        setPhoneHint(result.phoneHint);
        // Dropped as soon as it has been spent. No reason for it to sit in a
        // React state tree for the next ten minutes.
        setPassword("");
        setMode("otp");
      }
      // `step: "done"` needs nothing — the context has set the admin and the
      // router swaps this screen out.
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    void run(async () => {
      try {
        await verifyOtp(challenge, code);
      } catch (err) {
        setCode("");
        // The API says "enter your password again" when the challenge is
        // spent, expired, or out of attempts. Dropping back to step one beats
        // leaving a dead code box on screen.
        if (err instanceof Error && /password again/i.test(err.message)) {
          setChallenge(null);
          setPhoneHint(null);
          setMode("password");
        }
        throw err;
      }
    });
  };

  const submitForgot = (e: FormEvent) => {
    e.preventDefault();
    // `run` leaves the mode alone when the call throws, so a rejected address
    // stays on THIS screen with the reason under the field — rather than
    // advancing to a code box for an SMS that was never sent.
    void run(async () => {
      const res = await forgotPassword(email);
      setChallenge(res.challenge);
      setPhoneHint(res.phoneHint);
      setMode("reset");
    });
  };

  const submitReset = (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;

    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }

    // A reset is usually a response to "somebody has my password", which is
    // the worst moment to accept a weaker one than the invitation demanded.
    if (!passwordsAcceptable(password, confirm, { email })) {
      setError("That password doesn't meet the requirements below yet.");
      return;
    }

    void run(async () => {
      try {
        await resetPassword(challenge, code, password, confirm);
      } catch (err) {
        setCode("");
        if (err instanceof Error && /expired|start again/i.test(err.message)) {
          goTo("forgot");
        }
        throw err;
      }
    });
  };

  const resend = () =>
    void run(async () => {
      if (!challenge) return;
      const res = await resendOtp(challenge);
      setPhoneHint(res.phoneHint);
      setNotice("A new code is on its way.");
    });

  // ── Shared bits ──────────────────────────────────────────────────────────
  const messages = (
    <>
      {error && <p className="form-error">{error}</p>}
      {notice && (
        <p className="field-hint" style={{ marginBottom: 12 }}>
          {notice}
        </p>
      )}
    </>
  );

  const codeField = (label: string) => (
    <div className="field">
      <label htmlFor="code">{label}</label>
      <input
        id="code"
        ref={codeInput}
        className="input mono"
        style={{ fontSize: 20, letterSpacing: "0.3em", textAlign: "center" }}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputMode="numeric"
        // Lets iOS and Android offer the code straight from the notification
        // rather than making someone switch apps to read it.
        autoComplete="one-time-code"
        placeholder="000000"
        required
      />
    </div>
  );

  // ── Step two of signing in ───────────────────────────────────────────────
  if (mode === "otp") {
    return (
      <div className="login">
        <form className="login-card" onSubmit={submitCode}>
          <div className="brand-mark" />
          <h1>Check your phone</h1>
          <p className="sub">
            We texted a 6-digit code to{" "}
            <strong style={{ color: "var(--text)" }}>{phoneHint}</strong>. It
            expires in 10 minutes.
          </p>

          {messages}
          {codeField("Sign-in code")}

          <button
            className="btn btn-primary"
            style={{ width: "100%", marginTop: 6 }}
            disabled={busy || code.length < 6}
          >
            {busy ? "Checking…" : "Sign in"}
          </button>

          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 14,
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              className="btn btn-sm"
              onClick={resend}
              disabled={busy}
            >
              Resend code
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => goTo("password")}
            >
              Start over
            </button>
          </div>

          <div className="login-switch">
            No code arriving? The number is set on your account — ask the master
            admin to check it.
          </div>
        </form>
      </div>
    );
  }

  // ── Forgotten password, step one ─────────────────────────────────────────
  if (mode === "forgot") {
    return (
      <div className="login">
        <form className="login-card" onSubmit={submitForgot}>
          <div className="brand-mark" />
          <h1>Reset your password</h1>
          <p className="sub">
            We'll text a code to the mobile on your admin account. No password
            needed — that is the point.
          </p>

          {messages}

          <div className="field">
            <label htmlFor="forgot-email">Work email</label>
            <input
              id="forgot-email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              placeholder={
                config?.allowedDomains[0]
                  ? `you@${config.allowedDomains[0]}`
                  : "you@company.com"
              }
              required
              autoFocus
            />
            {config && config.allowedDomains.length > 0 && (
              <span className="field-hint">
                Must be{" "}
                {config.allowedDomains.map((d) => `@${d}`).join(" or ")} — the
                address your account was invited on.
              </span>
            )}
          </div>

          <button
            className="btn btn-primary"
            style={{ width: "100%", marginTop: 6 }}
            disabled={busy}
          >
            {busy ? "Sending…" : "Send me a code"}
          </button>

          <div className="login-switch">
            <button type="button" onClick={() => goTo("password")}>
              Back to sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Forgotten password, step two ─────────────────────────────────────────
  if (mode === "reset") {
    return (
      <div className="login">
        <form className="login-card" onSubmit={submitReset}>
          <div className="brand-mark" />
          <h1>Choose a new password</h1>
          <p className="sub">
            We texted a 6-digit code to{" "}
            <strong style={{ color: "var(--text)" }}>{phoneHint}</strong>. Enter
            it, then set a new password.
          </p>

          {messages}

          {/* Hidden but present: without it a password manager saves the new
              credential against no account and cannot offer it back later. */}
          <input
            type="email"
            value={email}
            autoComplete="username"
            readOnly
            hidden
          />

          {codeField("Code from the text message")}

          <PasswordFields
            label="New password"
            password={password}
            confirm={confirm}
            onPassword={setPassword}
            onConfirm={setConfirm}
            identity={{ email }}
          />

          <button
            className="btn btn-primary"
            style={{ width: "100%", marginTop: 6 }}
            disabled={
              busy ||
              code.length < 6 ||
              !passwordsAcceptable(password, confirm, { email })
            }
          >
            {busy ? "Saving…" : "Set password & sign in"}
          </button>

          <div className="login-switch">
            Every device signed in to this account will be signed out.{" "}
            <button type="button" onClick={() => goTo("password")}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Step one of signing in ───────────────────────────────────────────────
  return (
    <div className="login">
      <form className="login-card" onSubmit={submitPassword}>
        <div className="brand-mark" />
        <h1>JK Maxx Admin</h1>
        <p className="sub">Visualiser analytics and customer console.</p>

        {/* Without a registration form, an empty AdminUser table is a locked
            door with no visible key. The command that cuts one is printed
            here rather than left in a README nobody opens at 9pm. */}
        {config?.noAdmins && (
          <div className="banner banner-warning" style={{ marginBottom: 16 }}>
            <div>
              <strong>No admin account exists yet</strong>
              Create the master account on the API server, then sign in here:
              <code
                className="mono"
                style={{ display: "block", marginTop: 6, lineHeight: 1.6 }}
              >
                npm run admin:create -- --email you@jkmaxx.com --name "Your
                Name" --phone 9876543210
              </code>
            </div>
          </div>
        )}

        {messages}

        <div className="field">
          <label htmlFor="email">Work email</label>
          <input
            id="email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            placeholder={
              config?.allowedDomains[0]
                ? `you@${config.allowedDomains[0]}`
                : "you@company.com"
            }
            required
            autoFocus
          />
        </div>

        <div className="field">
          <label
            htmlFor="password"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <span>Password</span>
            <button
              type="button"
              onClick={() => goTo("forgot")}
              style={{
                background: "none",
                border: 0,
                padding: 0,
                color: "var(--accent)",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Forgot password?
            </button>
          </label>
          <input
            id="password"
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button
          className="btn btn-primary"
          style={{ width: "100%", marginTop: 6 }}
          disabled={busy}
        >
          {busy
            ? "Checking…"
            : config?.twoFactor === false
              ? "Sign in"
              : "Continue"}
        </button>

        {config?.twoFactor && (
          <p className="field-hint" style={{ marginTop: 12 }}>
            We'll text a code to the mobile on your account.
          </p>
        )}

        {/* Said plainly rather than hidden. An admin who expects a code and
            never gets one should be able to see that two-step is switched off
            rather than assume the SMS is late. */}
        {config?.twoFactor === false && (
          <p className="field-hint" style={{ marginTop: 12 }}>
            Two-step sign-in is currently switched off on this API
            (ADMIN_REQUIRE_2FA).
          </p>
        )}

        {/* Only shown when the server says the bypass is configured, so nobody
            wastes ten minutes on credentials that were never enabled. The
            address is not printed — the server does not return it. */}
        {config?.bypassEnabled && (
          <p className="field-hint" style={{ marginTop: 8 }}>
            A developer bypass account is enabled on this API
            (ADMIN_BYPASS_EMAIL). It skips both the domain gate and the texted
            code — unset it in production.
          </p>
        )}

        <div className="login-switch">
          Access is by invitation. Ask the master admin to invite you — there
          is no sign-up.
        </div>
      </form>
    </div>
  );
}
